﻿import { Injectable, Inject } from "@fiqas/angular-utils";
import { FIQAssist } from "../app";
import { LogService } from "./logService";
import { TranslationService } from "./TranslationService";
import { CSharpException, ParsedResult, ValidationResult } from "../models/base";
import { Application } from "../components/application";
import { StartVersion } from "../models/version";

export type dialogType = 'info' | 'warning' | 'error';

@Injectable({
    module: FIQAssist,
    selector: DialogService.selector
})
export class DialogService {
    public static selector: string = 'dialogManager';

    constructor(
        @Inject('$uibModal') private $modal: angular.ui.bootstrap.IModalService,
        @Inject() private logService: LogService,
        @Inject() private translationService: TranslationService
    ) {
    }

    public async showMessageDialog(type: dialogType, title: string, message: string
        , { titleArgs, messageArgs, translate, onclose }
            : { titleArgs?: string[], messageArgs?: string[], translate?: boolean, errorResult?: ParsedResult, onclose?: () => void }
            = { translate: true }) {
        if (translate) {
            title = titleArgs ? this.translationService.Format(title, titleArgs) : this.translationService.Translate(title);
            message = messageArgs ? this.translationService.Format(message, messageArgs) : this.translationService.Translate(message);
        }
        return this.$modal.open({
            templateUrl: 'messageDialog.html',
            controllerAs: "$messageModal",
            controller: MessageBox,
            backdrop: 'static',
            resolve: {
                data: () => {
                    return {
                        type: type,
                        title: title,
                        message: message,
                        onclose: (onclose) ? onclose : () => {}
                    }
                }
            }
        });
    }

    public async showConfirmationDialog(type: dialogType, title: string, message: string, buttonOk?: string, buttonCancel?: string
        , { titleArgs, messageArgs, translate, onclose, ondismiss }
                : { titleArgs?: string[], messageArgs?: string[], translate?: boolean, errorResult?: ParsedResult, onclose?: () => void, ondismiss?: () => void }
                = { translate: true }) {
        if (translate) {
            title = titleArgs ? this.translationService.Format(title, titleArgs) : this.translationService.Translate(title);
            message = messageArgs ? this.translationService.Format(message, messageArgs) : this.translationService.Translate(message);
            buttonOk = buttonOk ? this.translationService.Translate(buttonOk): this.translationService.Translate('DIALOG_CONFIRM_OK');
            buttonCancel = buttonCancel ? this.translationService.Translate(buttonCancel): this.translationService.Translate('DIALOG_CONFIRM_CANCEL');
        }

        return this.$modal.open({
            templateUrl: 'confirmationDialog.html',
            controllerAs: "$confirmationModal",
            controller: ConfirmationBox,
            backdrop: 'static',
            resolve: {
                data: () => {
                    return {
                        type: type,
                        title: title,
                        message: message,
                        buttonOk: buttonOk,
                        buttonCancel: buttonCancel,
                        onclose: (onclose) ? onclose : () => {},
                        ondismiss: (ondismiss) ? ondismiss : () => {}
                    }
                }
            }
        }).result;
    }

    public async showErrorDialog(type: dialogType, title: string, message: string
        , { information, titleArgs, messageArgs, informationArgs, translate, errorResult }
            : { information?: string, titleArgs?: string[], messageArgs?: string[], informationArgs?: string[], translate?: boolean, errorResult?: ParsedResult }
            = { translate: true }) {

        if (translate) {
            title = titleArgs ? this.translationService.Format(title, titleArgs) : this.translationService.Translate(title);
            message = messageArgs ? this.translationService.Format(message, messageArgs) : this.translationService.Translate(message);
            information = informationArgs ? this.translationService.Format(information, informationArgs) : this.translationService.Translate(information);
        }
        return this.$modal.open({
            templateUrl: 'errorDialog.html',
            controllerAs: "$errorModal",
            controller: ErrorBox,
            backdrop: 'static',
            resolve: {
                data: () => {
                    return {
                        type: type,
                        title: title,
                        message: message,
                        exception: (errorResult && errorResult.exception) ? errorResult.exception : null,
                        validation: (errorResult && errorResult.validation) ? errorResult.validation : null,
                        seqrequestid: (errorResult && errorResult.seqrequestid) ? errorResult.seqrequestid : null,
                        sequrl: (errorResult && errorResult.sequrl) ? errorResult.sequrl : null
                    }
                }
            }
        });
    }

    public async showAbout(application: Application) {
        return this.$modal.open({
            animation: false,
            templateUrl: 'aboutDialog.html',
            controller: AboutComponent,
            controllerAs: '$modal',
            resolve: {
                page: () => application
            }
        });
    }
}

export class MessageBox {
    constructor(
        @Inject('$uibModalInstance') private $modalInstance: angular.ui.bootstrap.IModalServiceInstance,
        @Inject('data') public data: { type: string, title: string, message: string, onclose: () => void }
    ) {
    }

    public Ok() {
        this.$modalInstance.close();
        this.data.onclose();
    }
}

export class ConfirmationBox {
    constructor(
        @Inject('$uibModalInstance') private $modalInstance: angular.ui.bootstrap.IModalServiceInstance,
        @Inject('data') public data: { type: string, title: string, message: string, buttonOk: string, buttonCancel: string, onclose: () => void },
        @Inject('$window') private $window: ng.IWindowService
    ) {
        // manually remove the 'modal-open' class, as the $uibModal does not do this correctly when the the big editorscreen is open too.
        this.$modalInstance.closed.then(() => {
            if (this.$window.document.body) {
                this.$window.document.body.classList.remove('modal-open');
            }
        });
    }

    public Ok() {
        this.$modalInstance.close();
        this.data.onclose();
    }
    public Cancel() {
        this.$modalInstance.dismiss();
    }
}

export class ErrorBox {
    constructor(
        @Inject('$uibModalInstance') private $modalInstance: angular.ui.bootstrap.IModalServiceInstance,
        @Inject('data') public data: { error: Error, validation: ValidationResult, exception: CSharpException, seqrequestid: string, sequrl: string },
    ) {
    }

    public showInformation: boolean;

    public Ok() {
        this.$modalInstance.close();
    }
}

export class AboutComponent {
    static controllerId = 'AboutComponent';
    public apiVersion: StartVersion;

    constructor(
        @Inject('$uibModalInstance') private $modalInstance: angular.ui.bootstrap.IModalServiceInstance,
        @Inject('page') public page: Application
    ) {
        this.apiVersion = page.apiVersion
        if (!this.apiVersion) {
            page.recieveApiVersion((value: StartVersion) => this.apiVersion = value);
        }
    }

    public Close() {
        this.$modalInstance.close();
    }
}
